import React from 'react'
import { Typography, Flex, Divider, CheckboxChangeEvent } from 'antd'
import {
  CardDescription,
  CardHeader,
  SectionCard,
} from '@/shared/ui/layout/styled'
import { Icon } from '@/shared/ui/Icon/Icon'
import {
  AccountsSection,
  SelectedData,
  SelectedCheck,
  SelectedInfo,
  SelectedOptions,
  SelectedOption,
  SelectedOptionLabel,
} from './styled'
import { WarningAlert } from '@/shared/ui/components/Alert'
import { Checkbox } from '@/shared/ui/components/Checkbox'
import { AccountsGrid } from '@/widgets/accountsGrid'
import type { Account } from '@/widgets/accountsGrid'
import { SYSTEM_TYPE, useSystemStore } from '@/app/store/systemStore'

const { Text, Title } = Typography

export interface AccountSelectionProps {
  selectedAccount: string
  onAccountSelect: (accountId: string) => void
  accounts: Account[]
  isLoading?: boolean
  isMyAccountSelected: boolean
  onMyAccountSelect: (checked: boolean) => void
}

export const AccountSelectionCard: React.FC<AccountSelectionProps> = ({
  selectedAccount,
  onAccountSelect,
  accounts,
  isLoading = false,
  isMyAccountSelected,
  onMyAccountSelect,
}) => {
  const selectedAccountData = accounts.find((acc) => acc.id === selectedAccount)
  const showAccountAlert = selectedAccount && !isMyAccountSelected
  const { selectedSystem } = useSystemStore()
  const isGlonassoft = selectedSystem === SYSTEM_TYPE.GLONASSOFT

  return (
    <SectionCard>
      <CardHeader gap={8}>
        <Icon name="account_tree" size={32} />
        <Title level={2}>
          {isGlonassoft
            ? 'Выберите клиента в которого необходимо перенести данные'
            : 'Выбор учетной записи для переноса данных'}
        </Title>
      </CardHeader>

      <Flex vertical gap={16}>
        <Checkbox
          checked={isMyAccountSelected}
          onChange={(e: CheckboxChangeEvent) =>
            onMyAccountSelect(e.target.checked)
          }
        >
          {isGlonassoft ? (
            <Text strong>
              Перенести данные в мою клиентскую запись (партнера)
            </Text>
          ) : (
            <Text strong>Перенести данные в мою учетную запись (партнера)</Text>
          )}
        </Checkbox>
        {isMyAccountSelected && (
          <WarningAlert
            title={
              <>
                <strong>Внимание!</strong> Не рекомендуется переносить данные в
                учетную запись партнера.
                <br />
                Это может привести к смешиванию данных клиентов и усложнить
                управление системой.
              </>
            }
            icon={<Icon name="warning_alert" size={16} />}
          />
        )}
      </Flex>

      {!isMyAccountSelected && (
        <AccountsSection vertical gap={32}>
          <Divider />
          <CardDescription>
            Выберите клиента, в которого будут переноситься данные. Для
            партнеров доступен перенос в любого клиента.
          </CardDescription>

          <AccountsGrid
            accounts={accounts}
            selectedAccounts={[selectedAccount]}
            onAccountSelect={onAccountSelect}
            isLoading={isLoading}
            searchLabel={'Клиенты'}
          />

          <Divider />

          {showAccountAlert && selectedAccountData && (
            <SelectedData gap={8}>
              <SelectedCheck name="account_check" size={24} />
              <SelectedInfo vertical>
                <Text strong>
                  Выбранный клиент: {selectedAccountData.title}
                </Text>
                <SelectedOptions gap="8px 32px" wrap>
                  <SelectedOption gap={4}>
                    <SelectedOptionLabel>Тип:</SelectedOptionLabel>
                    <Text>{selectedAccountData.type}</Text>
                  </SelectedOption>
                  <SelectedOption gap={4}>
                    <SelectedOptionLabel>Полное имя:</SelectedOptionLabel>
                    <Text>{selectedAccountData.username}</Text>
                  </SelectedOption>
                </SelectedOptions>
              </SelectedInfo>
            </SelectedData>
          )}
        </AccountsSection>
      )}
    </SectionCard>
  )
}
